import { Files, Logs, RefreshCw, ShieldCheck, ShieldX } from 'lucide-react'
import { useState } from 'react'
import { useBackupLogsQuery } from '../hooks/useBackupLogsQuery'
import { useTriggerBackupMutation } from '../hooks/useTriggerBackupMutation'
import BackupFiles from '../components/BackupFiles'
import BackupHistory from '../components/BackupLogs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/custom/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

export default function BackupDashboard() {
  const [activeTab, setActiveTab] = useState<string>('history')

  const { data: logs, isLoading: isLoadingLogs, refetch } = useBackupLogsQuery()
  const { mutateAsync: triggerBackup, isPending: isTriggeringBackup } =
    useTriggerBackupMutation()

  const lastLog = logs?.[0]
  const lastFailed = lastLog?.action === 'BACKUP_FAILURE'
  const lastSuccess = lastLog?.action === 'BACKUP_SUCCESS'

  const handleBackup = async () => {
    await triggerBackup()
    refetch()
  }

  return (
    <div className="py-8 space-y-10">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-1 flex items-center gap-2">
            Backup Dashboard
          </h1>
          <p className="text-gray-600">
            Monitor, download, and manage your application backups.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastFailed ? (
            <>
              <Button
                onClick={handleBackup}
                disabled={isTriggeringBackup}
                variant="destructive"
                className="flex items-center gap-2"
              >
                {isTriggeringBackup ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Retry Backup
                  </>
                )}
              </Button>
              <Badge color="red" variant="soft" size="sm">
                <div className="flex items-center gap-1">
                  <ShieldX className="h-4 w-4 shrink-0" />
                  <span className="whitespace-nowrap">Last backup failed</span>
                </div>
              </Badge>
            </>
          ) : lastSuccess ? (
            <Badge color="green" variant="soft" size="sm">
              <div className="flex items-center gap-1">
                <ShieldCheck className="h-4 w-4 shrink-0" />
                <span className="whitespace-nowrap">Backup succeeded</span>
              </div>
            </Badge>
          ) : null}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex gap-2 w-full">
          <TabsTrigger
            value="history"
            className="flex items-center justify-center gap-2 px-3 py-2 whitespace-nowrap"
          >
            <Logs className="h-4 w-4" />
            Backup History
          </TabsTrigger>
          <TabsTrigger
            value="files"
            className="flex items-center justify-center gap-2 px-3 py-2 whitespace-nowrap"
          >
            <Files className="h-4 w-4" />
            Backup Files
          </TabsTrigger>
        </TabsList>

        <TabsContent value="history" className="mt-6">
          <BackupHistory logs={logs || []} isLoading={isLoadingLogs} />
        </TabsContent>

        <TabsContent value="files" className="mt-6">
          <BackupFiles />
        </TabsContent>
      </Tabs>
    </div>
  )
}
